import { Injectable } from '@angular/core';
import { AttachmentInfo } from '../sdk/sdk';

export type FileType = 'pdf' | 'document' | 'image' | 'compressed' | 'video' | 'spreadsheet';

export interface FileBadge {
  iconClass: string;
  textClass: string;
}

@Injectable({
  providedIn: 'root',
})
export class FileUtilityService {
  private readonly imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'heic'];
  private readonly compressedExtensions = ['zip', 'rar', '7z', 'tar', 'gz'];
  private readonly videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'webm', 'm4v'];
  private readonly spreadsheetExtensions = ['xls', 'xlsx', 'csv', 'ods'];
  private readonly documentExtensions = ['doc', 'docx', 'txt', 'rtf'];

  /**
   * Extracts the file extension from a file name or URL
   * @param fileName - The file name or URL to extract extension from
   * @returns The file extension in lowercase, or empty string if not found
   */
  getFileExtension(fileName: string | null | undefined): string {
    if (!fileName) {
      return '';
    }
    const regex = /\.([a-zA-Z0-9]+)$/;
    const match = regex.exec(fileName);
    return match ? match[1].toLowerCase() : '';
  }

  /**
   * Checks if a file is an image based on its extension
   * @param attachment - The attachment to check
   * @returns True if the file is an image
   */
  isImageFile(attachment: AttachmentInfo): boolean {
    const extension = this.getFileExtension(attachment.fileName || attachment.description);
    return this.imageExtensions.includes(extension);
  }

  /**
   * Checks if a File object is an image based on its type
   * @param file - The File object to check
   * @returns True if the file is an image
   */
  isImageFileType(file: File): boolean {
    return file.type?.startsWith('image/') ?? false;
  }

  /**
   * Determines the file type category based on extension
   * @param attachment - The attachment to classify
   * @returns The file type category
   */
  getFileType(attachment: AttachmentInfo): FileType {
    const extension = this.getFileExtension(attachment.fileName || attachment.description);

    if (extension === 'pdf') {
      return 'pdf';
    }

    if (this.imageExtensions.includes(extension)) {
      return 'image';
    }

    if (this.compressedExtensions.includes(extension)) {
      return 'compressed';
    }

    if (this.videoExtensions.includes(extension)) {
      return 'video';
    }

    if (this.spreadsheetExtensions.includes(extension)) {
      return 'spreadsheet';
    }

    return 'document';
  }

  /**
   * Gets a file badge (icon and text class) for a File object
   * @param file - The File object to get badge for
   * @returns FileBadge with icon and text classes
   */
  getFileBadge(file: File): FileBadge {
    const ext = this.getFileExtensionFromFile(file).toLowerCase();
    const isImage = this.isImageFileType(file);
    const isVideo = file.type?.startsWith('video/') ?? false;

    if (isImage) {
      return {
        iconClass: 'bi-image',
        textClass: 'text-primary',
      };
    }

    if (ext === 'pdf') {
      return {
        iconClass: 'bi-filetype-pdf',
        textClass: 'text-danger',
      };
    }

    if (this.documentExtensions.includes(ext)) {
      return {
        iconClass: 'bi-file-earmark-word',
        textClass: 'text-secondary',
      };
    }

    if (this.spreadsheetExtensions.includes(ext)) {
      return {
        iconClass: 'bi-file-earmark-spreadsheet',
        textClass: 'text-success',
      };
    }

    if (this.compressedExtensions.includes(ext)) {
      return {
        iconClass: 'bi-file-zip',
        textClass: 'text-warning',
      };
    }

    if (isVideo || this.videoExtensions.includes(ext)) {
      return {
        iconClass: 'bi-file-earmark-play',
        textClass: 'text-primary',
      };
    }

    return {
      iconClass: 'bi-file',
      textClass: 'text-muted',
    };
  }

  /**
   * Extracts file extension from a File object
   * @param file - The File object
   * @returns The file extension in uppercase (max 3 chars)
   */
  private getFileExtensionFromFile(file: File): string {
    const parts = file.name?.split('.') ?? [];
    const ext = parts.length > 1 ? parts.pop() : null;
    if (ext) {
      return ext.substring(0, 3).toUpperCase();
    }
    if (file.type) {
      return file.type.split('/').pop()?.substring(0, 3).toUpperCase() ?? 'FILE';
    }
    return 'FILE';
  }
}

